import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, User, Building2, Shield, Calendar, FileText, CheckCircle,
  XCircle, Clock, AlertCircle, Eye, ExternalLink, X, Download, Mail,
  RefreshCw, ChevronRight, Award, Globe, FileCheck, Search, FolderOpen
} from 'lucide-react';
import api from '../../services/apiService';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_URL}${url}`;
};

// Dossier Overlay Component (same style as OEMProjectDetail)
const DossierOverlay = ({ technician, onClose }) => {
  const [activeDoc, setActiveDoc] = useState(null);
  const [docSearch, setDocSearch] = useState('');
  const documents = technician?.qualifications || [];

  const getDocStatus = (doc) => {
    if (doc.is_expired) return { status: 'expired', color: 'red', label: 'EXPIRED' };
    if (doc.expiry_date) {
      const daysUntil = Math.ceil((new Date(doc.expiry_date) - new Date()) / (1000 * 60 * 60 * 24));
      if (daysUntil <= 30) return { status: 'expiring', color: 'amber', label: 'EXPIRING SOON' };
    }
    return { status: 'valid', color: 'emerald', label: 'VALID' };
  };

  useEffect(() => {
    if (documents.length > 0 && !activeDoc) {
      const sorted = [...documents].sort((a, b) => {
        const aStatus = getDocStatus(a);
        const bStatus = getDocStatus(b);
        const order = { valid: 0, expiring: 1, expired: 2 };
        return order[aStatus.status] - order[bStatus.status];
      });
      setActiveDoc(sorted[0]);
    }
  }, [documents, activeDoc]);

  const filteredDocuments = documents.filter(doc => {
    if (!docSearch) return true;
    const search = docSearch.toLowerCase();
    const docName = (doc.qualification_type_name || doc.name || doc.document_name || '').toLowerCase();
    return docName.includes(search);
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (!technician) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-6 bg-slate-900/80 backdrop-blur-sm">
      <div className="bg-white w-full max-w-7xl h-full max-h-[92vh] rounded-3xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-white">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-slate-900 rounded-2xl flex items-center justify-center text-orange-500 shadow-lg">
              <FileCheck className="h-6 w-6" />
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">Technical Dossier: {technician.full_name}</h3>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">Personnel Certification Database</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center bg-slate-100 hover:bg-red-50 text-slate-400 hover:text-red-500 rounded-xl transition-all"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content - Two Panels */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - Document List */}
          <div className="w-[400px] border-r border-slate-100 bg-slate-50/50 p-5 space-y-5 overflow-y-auto">
            {/* User Info Card */}
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-black text-xl shadow-lg">
                {technician.full_name?.charAt(0)?.toUpperCase() || 'T'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-base font-black text-slate-900 truncate">{technician.full_name}</div>
                <div className="text-[10px] font-bold text-orange-500 uppercase mt-1">{technician.company_name}</div>
                {technician.email && (
                  <div className="text-[10px] text-slate-400 mt-1 truncate">{technician.email}</div>
                )}
              </div>
            </div>

            {/* Authorization Badge */}
            {technician.authorization && (
              <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center text-white shadow-md">
                    <Shield className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-black text-emerald-900 uppercase">Authorized - Level {technician.authorization.level_code}</p>
                    <p className="text-[10px] text-emerald-600 font-medium">
                      Valid until: {technician.authorization.valid_until ? formatDate(technician.authorization.valid_until) : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Document Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={docSearch}
                onChange={(e) => setDocSearch(e.target.value)}
                placeholder="Search documents..."
                className="w-full h-10 pl-10 pr-4 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-xs font-medium"
              />
            </div>

            {/* Documents List */}
            <div>
              <div className="flex justify-between items-center mb-3 px-1">
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Credentials Ledger</h4>
                <span className="text-[9px] font-black bg-slate-200 px-2 py-1 rounded-lg uppercase">{filteredDocuments.length} / {documents.length}</span>
              </div>

              {filteredDocuments.length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
                  <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-xs font-bold text-slate-500">{docSearch ? 'No matching documents' : 'No documents uploaded'}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {[...filteredDocuments].sort((a, b) => {
                    const aStatus = getDocStatus(a);
                    const bStatus = getDocStatus(b);
                    const order = { valid: 0, expiring: 1, expired: 2 };
                    return order[aStatus.status] - order[bStatus.status];
                  }).map((doc) => {
                    const docStatus = getDocStatus(doc);
                    const isActive = activeDoc?.id === doc.id;

                    const borderColor = isActive
                      ? 'border-orange-600'
                      : docStatus.status === 'valid'
                        ? 'border-l-4 border-l-emerald-500 border-slate-200'
                        : docStatus.status === 'expiring'
                          ? 'border-l-4 border-l-amber-500 border-slate-200'
                          : 'border-l-4 border-l-red-500 border-slate-200';

                    return (
                      <button
                        key={doc.id}
                        onClick={() => setActiveDoc(doc)}
                        className={`w-full text-left p-4 rounded-2xl border transition-all flex items-center gap-4 ${
                          isActive
                            ? 'bg-orange-500 border-orange-600 text-white shadow-lg shadow-orange-500/20'
                            : `bg-white ${borderColor} hover:shadow-md`
                        }`}
                      >
                        <FileText className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-white' : docStatus.status === 'valid' ? 'text-emerald-500' : docStatus.status === 'expiring' ? 'text-amber-500' : 'text-red-500'}`} />
                        <div className="overflow-hidden flex-1">
                          <div className={`text-xs font-black uppercase tracking-tight truncate ${isActive ? 'text-white' : 'text-slate-800'}`}>
                            {doc.qualification_type_name || doc.name || doc.document_name || 'Document'}
                          </div>
                          <div className={`text-[9px] font-bold mt-1 ${isActive ? 'text-white/70' : 'text-slate-400'}`}>
                            EXP: {doc.expiry_date ? formatDate(doc.expiry_date) : 'No expiry'}
                          </div>
                        </div>
                        {!isActive && (
                          <span className={`text-[8px] font-black uppercase px-1.5 py-0.5 rounded ${
                            docStatus.status === 'valid' ? 'bg-emerald-100 text-emerald-600' :
                            docStatus.status === 'expiring' ? 'bg-amber-100 text-amber-600' :
                            'bg-red-100 text-red-600'
                          }`}>{docStatus.label}</span>
                        )}
                        {isActive && <ChevronRight className="h-4 w-4 text-white flex-shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Document Preview */}
          <div className="flex-1 p-6 bg-white overflow-y-auto">
            {activeDoc ? (
              <div className="h-full flex flex-col">
                {/* Document Header */}
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h4 className="text-xl font-black text-slate-900 tracking-tight">{activeDoc.qualification_type_name || activeDoc.name || activeDoc.document_name || 'Document'}</h4>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Certificate ID: #{activeDoc.id || 'N/A'}</p>
                  </div>
                  <div className="flex gap-2">
                    {(activeDoc.document || activeDoc.certificate_file || activeDoc.document_url) && (
                      <a
                        href={getImageUrl(activeDoc.document || activeDoc.certificate_file || activeDoc.document_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-orange-500 transition-all shadow-lg"
                      >
                        <Download className="h-4 w-4" /> Export PDF
                      </a>
                    )}
                  </div>
                </div>

                {/* Status Badge */}
                {(() => {
                  const docStatus = getDocStatus(activeDoc);
                  return (
                    <div className={`mb-4 p-4 border-2 rounded-2xl flex items-center justify-between ${
                      docStatus.color === 'emerald' ? 'border-emerald-200 bg-emerald-50' :
                      docStatus.color === 'amber' ? 'border-amber-200 bg-amber-50' :
                      'border-red-200 bg-red-50'
                    }`}>
                      <div className="flex items-center gap-3">
                        <CheckCircle className={`h-6 w-6 ${
                          docStatus.color === 'emerald' ? 'text-emerald-500' :
                          docStatus.color === 'amber' ? 'text-amber-500' :
                          'text-red-500'
                        }`} />
                        <div>
                          <div className={`text-xs font-black uppercase ${
                            docStatus.color === 'emerald' ? 'text-emerald-700' :
                            docStatus.color === 'amber' ? 'text-amber-700' :
                            'text-red-700'
                          }`}>Certificate {docStatus.label}</div>
                          <div className={`text-[9px] font-bold ${
                            docStatus.color === 'emerald' ? 'text-emerald-600' :
                            docStatus.color === 'amber' ? 'text-amber-600' :
                            'text-red-600'
                          }`}>
                            {activeDoc.expiry_date ? `Expires: ${formatDate(activeDoc.expiry_date)}` : 'No expiry date'}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-[8px] font-black uppercase ${
                          docStatus.color === 'emerald' ? 'text-emerald-400' :
                          docStatus.color === 'amber' ? 'text-amber-400' :
                          'text-red-400'
                        }`}>Status</div>
                        <div className={`text-lg font-black ${
                          docStatus.color === 'emerald' ? 'text-emerald-600' :
                          docStatus.color === 'amber' ? 'text-amber-600' :
                          'text-red-600'
                        }`}>{docStatus.label}</div>
                      </div>
                    </div>
                  );
                })()}

                {/* Document Preview Area */}
                <div className="flex-1 bg-slate-50 border-4 border-slate-100 rounded-3xl p-4 relative overflow-hidden min-h-[400px]">
                  {(activeDoc.document || activeDoc.certificate_file || activeDoc.document_url) ? (
                    <iframe
                      src={getImageUrl(activeDoc.document || activeDoc.certificate_file || activeDoc.document_url)}
                      className="w-full h-full min-h-[350px] rounded-xl bg-white shadow-lg"
                      title="Document Preview"
                    />
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center">
                      <div className="bg-white shadow-xl rounded-xl p-8 max-w-md text-center">
                        <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                          <FileText className="h-8 w-8 text-slate-400" />
                        </div>
                        <h5 className="text-sm font-black text-slate-900 mb-2">No Document File</h5>
                        <p className="text-xs text-slate-500 mb-4">This certificate entry has no attached document file.</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-slate-300 gap-4">
                <Eye className="h-16 w-16 opacity-20" />
                <p className="text-xs font-black uppercase tracking-widest">Select a document to preview</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const OEMNominationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [nomination, setNomination] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [showDossier, setShowDossier] = useState(false);

  useEffect(() => {
    fetchNomination();
  }, [id]);

  const fetchNomination = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/oem/nominations/${id}/`);
      setNomination(response.data);
    } catch (err) {
      console.error('Error fetching nomination:', err);
      toast.error('Failed to load nomination');
      navigate('/oem/nominations');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!window.confirm('Are you sure you want to approve this nomination?')) return;

    setProcessing(true);
    try {
      await api.post(`/oem/nominations/${id}/approve/`);
      toast.success('Nomination approved successfully');
      fetchNomination();
    } catch (err) {
      console.error('Error approving nomination:', err);
      toast.error(err.response?.data?.error || 'Failed to approve nomination');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    setProcessing(true);
    try {
      await api.post(`/oem/nominations/${id}/reject/`, {
        rejection_reason: rejectionReason
      });
      toast.success('Nomination rejected');
      setShowRejectModal(false);
      setRejectionReason('');
      fetchNomination();
    } catch (err) {
      console.error('Error rejecting nomination:', err);
      toast.error(err.response?.data?.error || 'Failed to reject nomination');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusStyle = (status) => {
    const styles = {
      pending: { bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400' },
      submitted: { bg: 'bg-blue-50', text: 'text-blue-600', dot: 'bg-blue-500' },
      under_review: { bg: 'bg-amber-50', text: 'text-amber-600', dot: 'bg-amber-500' },
      approved: { bg: 'bg-emerald-50', text: 'text-emerald-600', dot: 'bg-emerald-500' },
      rejected: { bg: 'bg-red-50', text: 'text-red-600', dot: 'bg-red-500' },
      revoked: { bg: 'bg-red-50', text: 'text-red-600', dot: 'bg-red-500' },
      expired: { bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400' },
    };
    return styles[status] || styles.pending;
  };

  const getFileType = (url) => {
    if (!url) return 'unknown';
    const lower = url.toLowerCase();
    if (lower.includes('.pdf')) return 'pdf';
    if (lower.includes('.png') || lower.includes('.jpg') || lower.includes('.jpeg') || lower.includes('.gif') || lower.includes('.webp')) return 'image';
    return 'unknown';
  };

  const handlePreview = (url, name) => {
    if (!url) return;
    setPreviewDocument({ url: getImageUrl(url), name, type: getFileType(url) });
  };

  const getUserQualification = (docId) => {
    if (!nomination?.user_qualifications) return null;
    return nomination.user_qualifications.find(q => q.id === docId);
  };

  const canTakeAction = nomination && ['submitted', 'under_review'].includes(nomination.status);

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl p-10 flex items-center justify-center shadow-sm">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-slate-600 text-sm">Loading nomination...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!nomination) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl p-10 text-center shadow-sm">
          <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-8 w-8 text-red-500" />
          </div>
          <h2 className="text-lg font-black text-slate-900 mb-2">Nomination not found</h2>
          <button
            onClick={() => navigate('/oem/nominations')}
            className="text-orange-500 hover:text-orange-600 text-sm font-bold"
          >
            Back to Nominations
          </button>
        </div>
      </div>
    );
  }

  const statusStyle = getStatusStyle(nomination.status);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="p-5 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/oem/nominations')}
                className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 text-slate-600" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                  <Award className="h-5 w-5 text-violet-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-base font-black text-slate-900">{nomination.nomination_number}</h1>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold rounded-md uppercase ${statusStyle.bg} ${statusStyle.text}`}>
                      <span className={`w-1 h-1 rounded-full ${statusStyle.dot}`} />
                      {nomination.status?.replace('_', ' ')}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">{nomination.project_name} ({nomination.project_code})</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={fetchNomination}
                className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors"
                title="Refresh"
              >
                <RefreshCw className="h-4 w-4 text-slate-600" />
              </button>
              {canTakeAction && (
                <>
                  <button
                    onClick={() => setShowRejectModal(true)}
                    disabled={processing}
                    className="h-9 px-4 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 disabled:opacity-50 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide transition-colors border border-red-200"
                  >
                    <XCircle className="h-3.5 w-3.5" />
                    Reject
                  </button>
                  <button
                    onClick={handleApprove}
                    disabled={processing || !nomination.meets_requirements}
                    className="h-9 px-4 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 disabled:opacity-50 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide transition-colors shadow-lg shadow-emerald-500/20"
                    title={!nomination.meets_requirements ? 'Technician does not meet all requirements' : ''}
                  >
                    <CheckCircle className="h-3.5 w-3.5" />
                    {processing ? 'Processing...' : 'Approve'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-3 gap-5">
        {/* Left Column - Details */}
        <div className="space-y-4">
          {/* Technician Card */}
          <div className="bg-white rounded-2xl shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <User className="h-4 w-4 text-blue-600" />
                </div>
                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Technician</h3>
              </div>
              <button
                onClick={() => setShowDossier(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white rounded-lg text-[9px] font-bold uppercase tracking-wide hover:bg-orange-500 transition-colors"
              >
                <FolderOpen className="h-3 w-3" />
                Dossier
              </button>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-black text-lg shadow-lg">
                {nomination.user_name?.charAt(0)?.toUpperCase() || 'T'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-black text-slate-900 truncate">{nomination.user_name}</p>
                <p className="text-[10px] text-slate-500 flex items-center gap-1 mt-0.5">
                  <Mail className="h-3 w-3" />
                  {nomination.user_email}
                </p>
              </div>
            </div>
          </div>

          {/* Company Card */}
          <div className="bg-white rounded-2xl shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Building2 className="h-4 w-4 text-emerald-600" />
              </div>
              <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Company</h3>
            </div>
            <p className="text-sm font-bold text-slate-900">{nomination.company_name}</p>
            {nomination.company_representative_name && (
              <p className="text-[10px] text-slate-500 mt-1">Rep: {nomination.company_representative_name}</p>
            )}
          </div>

          {/* Authorization Level Card */}
          <div className="bg-white rounded-2xl shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                <Shield className="h-4 w-4 text-violet-600" />
              </div>
              <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Authorization Level</h3>
            </div>
            <div className="p-3 bg-violet-50 rounded-xl border border-violet-200">
              <p className="text-lg font-black text-violet-900">{nomination.authorization_level_code}</p>
              <p className="text-[10px] text-violet-600 font-medium mt-0.5">{nomination.authorization_level_name}</p>
            </div>
          </div>

          {/* Validity Period Card */}
          <div className="bg-white rounded-2xl shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                <Calendar className="h-4 w-4 text-orange-500" />
              </div>
              <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Validity Period</h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-slate-50 rounded-xl">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Start</p>
                <p className="text-sm font-bold text-slate-900 mt-0.5">{formatDate(nomination.date_start)}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">End</p>
                <p className="text-sm font-bold text-slate-900 mt-0.5">{formatDate(nomination.date_end)}</p>
              </div>
            </div>
          </div>

          {/* Timeline Card */}
          <div className="bg-white rounded-2xl shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                <Clock className="h-4 w-4 text-slate-600" />
              </div>
              <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Timeline</h3>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center py-2 border-b border-slate-100">
                <span className="text-[10px] font-bold text-slate-400 uppercase">Created</span>
                <span className="text-xs font-medium text-slate-700">{formatDateTime(nomination.created_at)}</span>
              </div>
              {nomination.submitted_at && (
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-[10px] font-bold text-slate-400 uppercase">Submitted</span>
                  <span className="text-xs font-medium text-slate-700">{formatDateTime(nomination.submitted_at)}</span>
                </div>
              )}
              {nomination.reviewed_at && (
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-[10px] font-bold text-slate-400 uppercase">Reviewed</span>
                  <span className="text-xs font-medium text-slate-700">{formatDateTime(nomination.reviewed_at)}</span>
                </div>
              )}
              {nomination.reviewed_by_name && (
                <div className="flex justify-between items-center py-2">
                  <span className="text-[10px] font-bold text-slate-400 uppercase">Reviewed by</span>
                  <span className="text-xs font-medium text-slate-700">{nomination.reviewed_by_name}</span>
                </div>
              )}
            </div>
          </div>

          {/* Rejection Reason */}
          {nomination.rejection_reason && (
            <div className="bg-red-50 rounded-2xl shadow-sm p-5 border border-red-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                  <XCircle className="h-4 w-4 text-red-600" />
                </div>
                <h3 className="text-[10px] font-black text-red-600 uppercase tracking-widest">Rejection Reason</h3>
              </div>
              <p className="text-sm text-red-700">{nomination.rejection_reason}</p>
            </div>
          )}
        </div>

        {/* Right Column - Documents */}
        <div className="col-span-2">
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden h-full">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <FileText className="h-4 w-4 text-blue-600" />
                </div>
                <h3 className="text-sm font-black text-slate-900">Document Verification</h3>
              </div>
              {nomination.meets_requirements ? (
                <span className="px-3 py-1 text-[10px] font-bold bg-emerald-100 text-emerald-700 rounded-lg uppercase tracking-wide">
                  All requirements met
                </span>
              ) : (
                <span className="px-3 py-1 text-[10px] font-bold bg-red-100 text-red-700 rounded-lg uppercase tracking-wide">
                  Missing requirements
                </span>
              )}
            </div>

            <div className="flex h-[calc(100vh-340px)] min-h-[500px]">
              {/* Required Documents */}
              <div className={`${previewDocument ? 'w-1/4' : 'w-1/2'} border-r border-slate-100 flex flex-col transition-all duration-300`}>
                <div className="p-3 border-b border-slate-100 bg-blue-50">
                  <h4 className="text-[10px] font-black text-blue-900 uppercase tracking-widest flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                    Required Documents
                  </h4>
                  <p className="text-[9px] text-blue-600 font-medium mt-0.5">All must be valid</p>
                </div>
                <div className="flex-1 overflow-y-auto p-3">
                  {nomination.required_documents && nomination.required_documents.length > 0 ? (
                    <div className="space-y-2">
                      {nomination.required_documents.map((doc) => {
                        const userQual = getUserQualification(doc.id);
                        const hasDoc = !!userQual;
                        const isExpired = userQual?.is_expired;

                        return (
                          <div
                            key={doc.id}
                            className={`p-3 rounded-xl border transition-all ${
                              hasDoc && !isExpired
                                ? 'bg-emerald-50 border-emerald-200'
                                : hasDoc && isExpired
                                ? 'bg-red-50 border-red-200'
                                : 'bg-amber-50 border-amber-200'
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                {hasDoc && !isExpired ? (
                                  <CheckCircle className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                                ) : hasDoc && isExpired ? (
                                  <Clock className="h-4 w-4 text-red-600 flex-shrink-0" />
                                ) : (
                                  <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                                )}
                                <div className="min-w-0 flex-1">
                                  <p className="font-bold text-[11px] truncate text-slate-900">{doc.name}</p>
                                  {userQual && (
                                    <p className={`text-[9px] font-medium ${isExpired ? 'text-red-600' : 'text-slate-500'}`}>
                                      Exp: {formatDate(userQual.expiry_date)}
                                    </p>
                                  )}
                                  {!hasDoc && (
                                    <p className="text-[9px] font-bold text-amber-700 uppercase">Missing</p>
                                  )}
                                </div>
                              </div>
                              {userQual?.certificate_file && (
                                <button
                                  onClick={() => handlePreview(userQual.certificate_file, doc.name)}
                                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                                    previewDocument?.name === doc.name
                                      ? 'bg-orange-500 text-white'
                                      : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                                  }`}
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-10 text-center">
                      <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mb-3">
                        <FileText className="h-6 w-6 text-slate-400" />
                      </div>
                      <p className="text-xs text-slate-500 font-medium">No specific documents required</p>
                    </div>
                  )}
                </div>
              </div>

              {/* All User Documents */}
              <div className={`${previewDocument ? 'w-1/4' : 'w-1/2'} border-r border-slate-100 flex flex-col transition-all duration-300`}>
                <div className="p-3 border-b border-slate-100 bg-violet-50">
                  <h4 className="text-[10px] font-black text-violet-900 uppercase tracking-widest flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-violet-500 rounded-full"></span>
                    All Documents ({nomination.user_qualifications?.length || 0})
                  </h4>
                  <p className="text-[9px] text-violet-600 font-medium mt-0.5">Technician's qualifications</p>
                </div>
                <div className="flex-1 overflow-y-auto p-3">
                  {nomination.user_qualifications && nomination.user_qualifications.length > 0 ? (
                    <div className="space-y-2">
                      {nomination.user_qualifications.map((qual) => (
                        <div
                          key={qual.id}
                          className={`p-3 rounded-xl border transition-all ${
                            qual.is_expired ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              {qual.is_expired ? (
                                <Clock className="h-4 w-4 text-red-500 flex-shrink-0" />
                              ) : (
                                <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                              )}
                              <div className="min-w-0 flex-1">
                                <p className="text-[11px] font-bold truncate text-slate-900">{qual.name}</p>
                                <p className={`text-[9px] font-medium ${qual.is_expired ? 'text-red-600' : 'text-slate-500'}`}>
                                  Exp: {formatDate(qual.expiry_date)}
                                </p>
                              </div>
                            </div>
                            {qual.certificate_file && (
                              <button
                                onClick={() => handlePreview(qual.certificate_file, qual.name)}
                                className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                                  previewDocument?.name === qual.name
                                    ? 'bg-orange-500 text-white'
                                    : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                                }`}
                              >
                                <Eye className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-10 text-center">
                      <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-3">
                        <AlertCircle className="h-6 w-6 text-amber-500" />
                      </div>
                      <p className="text-xs text-amber-700 font-medium">No documents uploaded</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Document Preview */}
              {previewDocument && (
                <div className="w-1/2 flex flex-col bg-slate-50">
                  <div className="p-3 border-b border-slate-200 bg-slate-100 flex items-center justify-between">
                    <h4 className="font-bold text-slate-900 truncate flex-1 text-xs uppercase tracking-wide">{previewDocument.name}</h4>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <a
                        href={previewDocument.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:text-orange-500 hover:border-orange-300 transition-colors"
                        title="Open in new tab"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                      <button
                        onClick={() => setPreviewDocument(null)}
                        className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:text-red-500 hover:border-red-300 transition-colors"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  <div className="flex-1 overflow-hidden">
                    {previewDocument.type === 'pdf' ? (
                      <iframe
                        src={previewDocument.url}
                        className="w-full h-full border-0"
                        title={previewDocument.name}
                      />
                    ) : previewDocument.type === 'image' ? (
                      <div className="w-full h-full flex items-center justify-center p-4 bg-slate-100">
                        <img
                          src={previewDocument.url}
                          alt={previewDocument.name}
                          className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                        />
                      </div>
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center p-4">
                        <div className="w-16 h-16 bg-slate-200 rounded-2xl flex items-center justify-center mb-4">
                          <FileText className="h-8 w-8 text-slate-400" />
                        </div>
                        <p className="text-slate-600 mb-4 text-sm font-medium">Preview not available</p>
                        <a
                          href={previewDocument.url}
                          download
                          className="px-4 py-2 bg-orange-500 text-white rounded-xl hover:bg-orange-600 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wide transition-colors"
                        >
                          <Download className="h-4 w-4" />
                          Download
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-black text-slate-900">Reject Nomination</h3>
                <p className="text-[10px] text-slate-500 font-medium mt-0.5">
                  This will reject the nomination for {nomination.user_name}
                </p>
              </div>
            </div>
            <div className="p-5">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
                Rejection Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 text-sm resize-none"
                placeholder="Please provide a reason for rejection..."
              />
            </div>
            <div className="p-4 border-t border-slate-100 flex justify-end gap-2 bg-slate-50">
              <button
                onClick={() => setShowRejectModal(false)}
                className="h-9 px-4 bg-white border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 text-[10px] font-bold uppercase tracking-wide transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={processing || !rejectionReason.trim()}
                className="h-9 px-4 bg-red-500 text-white rounded-xl hover:bg-red-600 disabled:opacity-50 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide transition-colors"
              >
                <XCircle className="h-3.5 w-3.5" />
                {processing ? 'Processing...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dossier Overlay */}
      {showDossier && nomination && (
        <DossierOverlay
          technician={{
            full_name: nomination.user_name,
            email: nomination.user_email,
            company_name: nomination.company_name,
            authorization: nomination.status === 'approved' ? {
              level_code: nomination.authorization_level_code,
              valid_until: nomination.date_end
            } : null,
            qualifications: nomination.user_qualifications?.map(q => ({
              ...q,
              qualification_type_name: q.name,
              document_url: q.certificate_file
            })) || []
          }}
          onClose={() => setShowDossier(false)}
        />
      )}
    </div>
  );
};

export default OEMNominationDetail;
